import { VESSEL_CARD_BALTIC_BENCHMARKS } from "../constants/vessel-card-baltic.constants";
import {
  VESSEL_CARD_DEFAULT_SCRUBBER_SELECTION,
  VESSEL_CARD_FUEL_GRADE_WITH_SCRUBBER,
  VESSEL_CARD_FUEL_GRADE_WITHOUT_SCRUBBER,
  VESSEL_CARD_OBS_ACTUAL_BASIS,
} from "../constants/vessel-card.constants";
import { getVesselCardMockResponse, vesselCardMockResponse } from "../mocks/vessel-card.mocks";
import {
  VesselCardCustomCpValues,
  VesselCardCpSource,
  VesselCardFuelGrade,
  VesselCardModal,
  VesselCardRequestStatus,
  VesselCardVesselOption,
  VesselCardVesselsResponse,
} from "../models/vessel-card.models";
import { getVesselCardDashboard, getVesselCardVessels } from "../services/vessel-card.service";
import {
  vesselCardBalticIndexAtom,
  vesselCardComparisonAtom,
  vesselCardCpSourceAtom,
  vesselCardMockEnabledAtom,
  vesselCardViewAtom,
} from "../state/vessel-card.atoms";
import { toVesselCardBalticRows } from "../utils/vessel-card-baltic.utils";
import { getVesselCardCpPerformance, toVesselCardCustomCpValues } from "../utils/vessel-card-cp-source.utils";
import {
  applyVesselCardScrubberSelection,
  getVesselCardScrubberSelectionHelp,
  getVesselCardScrubberWarning,
} from "../utils/vessel-card-scrubber.utils";
import axios from "axios";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const VESSEL_CARD_VESSEL_PAGE_SIZE = 50;
const VESSEL_CARD_VESSEL_SEARCH_DEBOUNCE_MS = 250;

const getMockVesselCardVessels = (query: string, offset: number): VesselCardVesselsResponse => {
  const normalizedQuery = query.trim().toLowerCase();
  const matchingVessels = vesselCardMockResponse.vessels.filter(
    (vessel) =>
      !normalizedQuery ||
      vessel.imo.includes(normalizedQuery) ||
      vessel.vesselName.toLowerCase().includes(normalizedQuery),
  );
  const vessels = matchingVessels.slice(offset, offset + VESSEL_CARD_VESSEL_PAGE_SIZE);
  const nextOffset = offset + vessels.length;

  return {
    vessels,
    hasMore: nextOffset < matchingVessels.length,
    nextOffset: nextOffset < matchingVessels.length ? nextOffset : null,
  };
};

// Owns request lifecycle and every bookmark-like choice used by the Vessel Card page.
// Aborts superseded loads so switching vessels or data sources cannot flash a stale card.
export const useVesselCard = () => {
  const useMockData = useAtomValue(vesselCardMockEnabledAtom);
  const navigate = useNavigate();
  const { imo: routeImo } = useParams<{ imo: string }>();
  const [searchParams] = useSearchParams();
  // The URL owns vessel selection so refreshes and browser history load the same vessel.
  // Old notification links keep opening request history after moving to the IMO sub-route.
  // The base route keeps the empty card visible so its search can select the first vessel.
  const linkedImo = searchParams.get("imo") ?? "";
  const selectedImo = routeImo && /^\d{7}$/.test(routeImo) ? routeImo : null;
  const redirectTo = selectedImo
    ? null
    : !routeImo && /^\d{7}$/.test(linkedImo)
      ? `/vessel-card/${linkedImo}?request=1`
      : routeImo
        ? "/vessels"
        : null;
  const [view, setView] = useAtom(vesselCardViewAtom);
  const [cpSource, setCpSource] = useAtom(vesselCardCpSourceAtom);
  const [comparison, setComparison] = useAtom(vesselCardComparisonAtom);
  const [balticIndex, setBalticIndex] = useAtom(vesselCardBalticIndexAtom);
  const [response, setResponse] = useState<typeof vesselCardMockResponse | null>(null);
  const [status, setStatus] = useState<VesselCardRequestStatus>("idle");
  const [error, setError] = useState("");
  const [searchEnabled, setSearchEnabled] = useState(!selectedImo);
  const [vessels, setVessels] = useState<VesselCardVesselOption[]>(() =>
    useMockData ? vesselCardMockResponse.vessels : [],
  );
  const [vesselQuery, setVesselQuery] = useState("");
  const [vesselStatus, setVesselStatus] = useState<VesselCardRequestStatus>("idle");
  const [vesselError, setVesselError] = useState("");
  const [hasMoreVessels, setHasMoreVessels] = useState(false);
  const [nextVesselOffset, setNextVesselOffset] = useState<number | null>(null);
  const [isLoadingMoreVessels, setIsLoadingMoreVessels] = useState(false);
  // Stores only the trader's proxy comparison choice; actual scrubber information stays in the response.
  // Assumes No is the initial proxy choice and resets it whenever another vessel or source loads.
  const [scrubberSelection, setScrubberSelection] = useState<boolean>(VESSEL_CARD_DEFAULT_SCRUBBER_SELECTION);
  const [customCp, setCustomCp] = useState<VesselCardCustomCpValues>(() =>
    toVesselCardCustomCpValues(
      vesselCardMockResponse.card.customCpPreviewPerformance ?? vesselCardMockResponse.card.performance,
    ),
  );
  const [comments, setComments] = useState<string[]>([]);
  const [modal, setModal] = useState<VesselCardModal>(() => (searchParams.get("request") === "1" ? "request" : null));
  const vesselLoadMoreAbortRef = useRef<AbortController | null>(null);
  const vesselLoadMorePendingRef = useRef(false);

  // Debounces live name-or-IMO searches and replaces the current virtual list with its first 50-row page.
  useEffect(() => {
    if (!searchEnabled) {
      return;
    }

    vesselLoadMoreAbortRef.current?.abort();
    vesselLoadMorePendingRef.current = false;
    setIsLoadingMoreVessels(false);
    const controller = new AbortController();
    setVessels([]);
    setVesselStatus("loading");
    setVesselError("");
    setHasMoreVessels(false);
    setNextVesselOffset(null);
    const timeout = window.setTimeout(async () => {
      try {
        const nextPage = useMockData
          ? getMockVesselCardVessels(vesselQuery, 0)
          : await getVesselCardVessels(vesselQuery.trim(), VESSEL_CARD_VESSEL_PAGE_SIZE, 0, controller.signal);

        if (!controller.signal.aborted) {
          setVessels(nextPage.vessels);
          setHasMoreVessels(nextPage.hasMore);
          setNextVesselOffset(nextPage.nextOffset);
          setVesselStatus("success");
        }
      } catch (requestError) {
        if (!axios.isCancel(requestError) && !controller.signal.aborted) {
          setVessels([]);
          setVesselStatus("error");
          setVesselError("Vessels could not be loaded.");
        }
      }
    }, VESSEL_CARD_VESSEL_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
      vesselLoadMoreAbortRef.current?.abort();
    };
  }, [searchEnabled, useMockData, vesselQuery]);

  useEffect(() => {
    if (!selectedImo) {
      setResponse(null);
      setStatus("idle");
      setError("");
      setModal(null);

      return;
    }

    const controller = new AbortController();
    setStatus("loading");
    setError("");

    setResponse(null);
    setModal(searchParams.get("request") === "1" ? "request" : null);

    const loadCard = async () => {
      try {
        const nextResponse = useMockData
          ? getVesselCardMockResponse(selectedImo)
          : await getVesselCardDashboard(selectedImo, controller.signal);

        if (controller.signal.aborted) {
          return;
        }

        if (nextResponse.card.vessel.imo !== selectedImo) {
          setStatus("error");
          setError(
            useMockData
              ? "Demonstration data is not available for this IMO. Select a demonstration vessel using search or switch to live data."
              : "The returned Vessel Card does not match the requested IMO. Select another vessel using search.",
          );

          return;
        }

        setResponse(nextResponse);
        setScrubberSelection(VESSEL_CARD_DEFAULT_SCRUBBER_SELECTION);
        setCustomCp(
          toVesselCardCustomCpValues(nextResponse.card.customCpPreviewPerformance ?? nextResponse.card.performance),
        );
        setComments(nextResponse.card.comments);
        setStatus("success");
      } catch (requestError) {
        if (!axios.isCancel(requestError) && !controller.signal.aborted) {
          setStatus("error");
          setResponse(null);
          setError("Vessel Card data could not be loaded. Use the search next to the title to select another vessel.");
        }
      }
    };

    void loadCard();

    return () => controller.abort();
  }, [selectedImo, useMockData, searchParams]);

  const selectVessel = useCallback(
    (imo: string) => {
      if (/^\d{7}$/.test(imo)) {
        navigate(`/vessel-card/${imo}`);
      }
    },
    [navigate],
  );

  const loadMoreVessels = useCallback(() => {
    if (!searchEnabled || !hasMoreVessels || nextVesselOffset === null || vesselLoadMorePendingRef.current) {
      return;
    }

    const controller = new AbortController();
    vesselLoadMoreAbortRef.current?.abort();
    vesselLoadMoreAbortRef.current = controller;
    vesselLoadMorePendingRef.current = true;
    setIsLoadingMoreVessels(true);
    setVesselError("");

    const loadNextVesselPage = async () => {
      try {
        const nextPage = useMockData
          ? getMockVesselCardVessels(vesselQuery, nextVesselOffset)
          : await getVesselCardVessels(
              vesselQuery.trim(),
              VESSEL_CARD_VESSEL_PAGE_SIZE,
              nextVesselOffset,
              controller.signal,
            );

        if (!controller.signal.aborted) {
          setVessels((current) => {
            const knownImos = new Set(current.map((vessel) => vessel.imo));

            return [...current, ...nextPage.vessels.filter((vessel) => !knownImos.has(vessel.imo))];
          });
          setHasMoreVessels(nextPage.hasMore);
          setNextVesselOffset(nextPage.nextOffset);
        }
      } catch (requestError) {
        if (!axios.isCancel(requestError) && !controller.signal.aborted) {
          setVesselError("More vessels could not be loaded.");
        }
      } finally {
        if (!controller.signal.aborted) {
          vesselLoadMorePendingRef.current = false;
          setIsLoadingMoreVessels(false);
        }
      }
    };

    void loadNextVesselPage();
  }, [hasMoreVessels, nextVesselOffset, searchEnabled, useMockData, vesselQuery]);

  // Derives corrected design figures once from the loaded VLSFO card, before CP, Baltic, ZN and TCO display them.
  // The loaded response is never edited, so No restores the original values and actual vessels remain unchanged.
  const scrubberCard = useMemo(
    () =>
      response && response.card.vessel.imo === selectedImo
        ? applyVesselCardScrubberSelection({ ...response.card, comments }, scrubberSelection)
        : null,
    [comments, response, scrubberSelection, selectedImo],
  );

  const hasPerformanceBasis = Boolean(scrubberCard?.performanceBasis?.trim());
  const isObservedActual =
    scrubberCard?.performanceBasis?.trim().toLowerCase() === VESSEL_CARD_OBS_ACTUAL_BASIS.toLowerCase();
  const scrubberSelectionForBasis = isObservedActual ? scrubberCard?.scrubberFitted ?? null : scrubberSelection;

  const card = useMemo(() => {
    if (!scrubberCard) {
      return null;
    }

    return {
      ...scrubberCard,
      performance: getVesselCardCpPerformance(scrubberCard, cpSource, customCp),
    };
  }, [cpSource, customCp, scrubberCard]);

  // Locks OBS_Actual to the backend value, including unknown values, and keeps unavailable basis data read-only.
  // Design selections also apply the approved HSFO consumption correction; fitted-vessel details remain source-only.
  const scrubberSelectionDisabled = status !== "success" || !hasPerformanceBasis || isObservedActual;
  const scrubberFitted = hasPerformanceBasis ? scrubberSelectionForBasis : null;
  const fuelGrade: VesselCardFuelGrade =
    scrubberFitted === null
      ? null
      : scrubberFitted
        ? VESSEL_CARD_FUEL_GRADE_WITH_SCRUBBER
        : VESSEL_CARD_FUEL_GRADE_WITHOUT_SCRUBBER;
  const scrubberSelectionHelp = getVesselCardScrubberSelectionHelp(card);
  const scrubberWarning = response ? getVesselCardScrubberWarning(response.card, scrubberSelection) : null;

  const selectScrubber = useCallback(
    (fitted: boolean) => {
      if (scrubberSelectionDisabled) {
        return;
      }

      setScrubberSelection(fitted);
    },
    [scrubberSelectionDisabled],
  );

  // Compares clean-hull performance with the selected Baltic ship, independently of CP input.
  // Derivation follows the loaded vessel directly, so switching vessels never retains old comparison values.
  const balticBenchmark = VESSEL_CARD_BALTIC_BENCHMARKS[balticIndex];
  const balticRows = useMemo(
    () => toVesselCardBalticRows(scrubberCard?.performance ?? [], balticBenchmark),
    [scrubberCard, balticBenchmark],
  );

  const selectCpSource = useCallback(
    (source: VesselCardCpSource) => {
      setCpSource(source);
    },
    [setCpSource],
  );

  const addComment = useCallback(
    (comment: string) => {
      const trimmedComment = comment.trim();

      if (useMockData && trimmedComment) {
        setComments((current) => [...current, trimmedComment]);
      }

      setModal(null);
    },
    [useMockData],
  );

  return {
    addComment,
    balticIndex,
    balticRows,
    balticBenchmarkAvailable: balticBenchmark.rows.length > 0,
    card,
    comparison,
    closeModal: () => setModal(null),
    cpSource,
    cpSourceMessage:
      cpSource === "imosInput" && !response?.card.imosInputPerformance
        ? "IMOS Input data is not available for this vessel."
        : card?.performance.some((row) => row.indicator === "Not available")
          ? "CP comparisons need positive expected speed and consumption plus the selected CP speed and consumption for each mode."
          : null,
    customCp,
    error,
    fuelGrade,
    hasMoreVessels,
    isLoadingMoreVessels,
    loadMoreVessels,
    modal,
    openModal: setModal,
    redirectTo,
    response,
    scrubberFitted,
    scrubberSelectionDisabled,
    scrubberSelectionHelp,
    scrubberWarning,
    selectScrubber,
    selectCpSource,
    selectedImo,
    setCustomCp,
    setBalticIndex,
    setComparison,
    selectVessel,
    setSearchEnabled,
    setVesselQuery,
    setView,
    status,
    useMockData,
    vesselError,
    vesselQuery,
    vesselStatus,
    vessels,
    view,
  };
};
